import type {
    RequiredCommonQueryParamsWithAuth,
    TrashByJobUuidAndFileUuidParams,
} from '~/@types/backend-types'
import { ThumbArea } from '~/@types/backend-types'
import { getCurrentLangDefinition, getDeviceProps } from '~/config/constants'
import { HostUrl } from '../toolbox'
import { getAuthToken, getStoredServiceDict } from '../externals'

export class ThumbService {
    private hostUrl: HostUrl
    private prefetchCaches: Set<URLstring> = new Set()

    constructor(hostname: string, authToken: string) {
        const commonQueryParams: RequiredCommonQueryParamsWithAuth = {
            auth: authToken,
            key: import.meta.env.VITE_API_KEY,
            client_v: import.meta.env.VITE_VERSION,
            device_id: getDeviceProps().device_id,
            app_lang: getCurrentLangDefinition().appLang,
        }
        this.hostUrl = new HostUrl(hostname, commonQueryParams)
    }

    // area[small, large] = [256, 1280]
    public getThumbUrl(
        jobID: JobID,
        fileID: FileID,
        area: ThumbArea = ThumbArea.SMALL,
    ): URLstring {
        return this.hostUrl.getPath(`/th/3/${fileID}`, {
            area,
            share: jobID,
        })
    }

    public getTimelineThumbUrl(
        fileID: FileID,
        area: ThumbArea = ThumbArea.SMALL,
    ): URLstring {
        return this.hostUrl.getPath(`/th/3/${fileID}`, {
            area,
            share: '',
        })
    }

    // area[small, large] = [256, 1280]
    public getTrashedThumbUrl(
        jobID: JobID,
        fileID: FileID,
        params: TrashByJobUuidAndFileUuidParams,
    ): URLstring {
        return this.hostUrl.getPath(`/th/3/trash/${jobID}/${fileID}`, params)
    }

    private _prefetchThumb(url: URLstring): void {
        if (this.prefetchCaches.has(url)) {
            return
        }

        const preload = new Image()
        preload.onload = () => this.prefetchCaches.add(url)
        preload.onerror = () => {
            this.prefetchCaches.delete(url)
        }
        preload.src = url
    }

    public prefetchTimelineThumb(
        fileID: FileID,
        area: ThumbArea = ThumbArea.SMALL,
    ) {
        return this._prefetchThumb(this.getTimelineThumbUrl(fileID, area))
    }
}

export function getThumbServiceForLoggedInUserDefaults(): ThumbService {
    const hosts = getStoredServiceDict()
    const token = getAuthToken()
    if (hosts === undefined || token === undefined) {
        throw new Error(
            'Service dict must be made available (by logging in etc)',
        )
    }
    return new ThumbService(hosts.thumbHost, token)
}
